import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Button from '@components/ui/Button';
import DatePicker from '@components/ui/DatePicker';
import Toggle from '@components/ui/Toggle';
import { useApiClient } from '@hooks/useApiClient';
import { getApiErrorMessage } from '@utils/api/ApiClient';
import { toast } from '@utils/toast';
import { useAppDispatch } from '../../../store';
import { fetchStores } from '../../../store/slices/storeManagementSlice';
import { getStoreBrandDetails } from '../../../services/reportsAdminService';
import {
    getBrandAgentConfig,
    getLoginPolicy,
    getMovementCountConfig,
    saveBrandAgentConfig,
    saveLoginPolicy,
    saveMovementCountConfig,
    getDailyNotificationSettings,
    updateDailyNotificationSettings,
    getStoreBrandOperationsConfig,
    updateStoreBrandOperationsConfig,
    getDashboardExpiry,
    updateDashboardExpiry,
    getStoresByBrand,
} from '../../../services/storeManagementService';
import {
    ALL_DASHBOARD_ROLES,
    NOTIFICATION_TYPES,
    NOTIFICATION_FILE_TYPES,
    type BrandAgentConfig,
    type LoginPolicy,
    type DashboardRole,
    type DailyNotificationSettings,
    type NotificationReportEntry,
    type StoreBrandOperationsConfig,
    type DashboardExpirySettings,
    type StoreSearchResult,
    type StoreLiveStatus,
} from '../../../types/api.types';
import {
    ModalShell,
    SettingsSection,
    SettingsRow,
    SettingsCheckboxGroup,
    SettingsSkeleton,
} from './shared';
import MakeTrialStoresLiveDialog from './MakeTrialStoresLiveDialog';

interface BrandSettingsModalProps {
    isOpen: boolean;
    storeBrandId: string;
    brandName: string;
    onClose: () => void;
}

type WhatsAppReportOption = {
    name: string;
    reportID: string;
};

const DEFAULT_AGENT_CONFIG: BrandAgentConfig = {
    checkDeliveryRequest: false,
    checkBulkOrderRequest: false,
};

const DEFAULT_LOGIN_POLICY: LoginPolicy = {
    disallowedRolesOnDashboard: [],
    disallowedRolesOnPOS: [],
    enforceSingleDevice: false,
};

const DEFAULT_DAILY_NOTIFICATION_SETTINGS: DailyNotificationSettings = {
    enabled: true,
    notificationReports: [],
};

const DEFAULT_BRAND_OPS_CONFIG: StoreBrandOperationsConfig = {
    phone: '',
    whatsappNumber: '',
};

const DEFAULT_DASHBOARD_EXPIRY: DashboardExpirySettings = {
    expiryDate: null,
    warningMessage: null,
};

const DASHBOARD_EXPIRY_WARNING_MAX_LENGTH = 240;
const STORE_STATUS_TRIAL: StoreLiveStatus = 'Trial';
const STORE_STATUS_ACTIVE: StoreLiveStatus = 'Active';

function normalizePhoneInput(value: string): string {
    return value.replace(/\D/g, '').slice(0, 10);
}

/** Format a local Date as YYYY-MM-DD for the dashboard-expiry API. */
function formatDateOnly(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/** Parse a YYYY-MM-DD string into a local Date (avoids UTC midnight shifts). */
function parseDateOnly(dateStr: string): Date {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
}

function startOfToday(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
}

function normalizeDashboardExpiry(
    settings: Partial<DashboardExpirySettings> | null | undefined
): DashboardExpirySettings {
    return {
        expiryDate: settings?.expiryDate ?? null,
        warningMessage: settings?.warningMessage ?? null,
    };
}

function dashboardExpiryEqual(a: DashboardExpirySettings, b: DashboardExpirySettings): boolean {
    return a.expiryDate === b.expiryDate && (a.warningMessage ?? '') === (b.warningMessage ?? '');
}

function normalizeLoginPolicy(policy: LoginPolicy): LoginPolicy {
    return {
        ...DEFAULT_LOGIN_POLICY,
        ...policy,
        disallowedRolesOnDashboard: policy.disallowedRolesOnDashboard ?? [],
        disallowedRolesOnPOS: policy.disallowedRolesOnPOS ?? [],
    };
}

function agentConfigEqual(a: BrandAgentConfig, b: BrandAgentConfig): boolean {
    return (
        a.checkDeliveryRequest === b.checkDeliveryRequest &&
        a.checkBulkOrderRequest === b.checkBulkOrderRequest
    );
}

function dailyNotificationSettingsEqual(
    a: DailyNotificationSettings,
    b: DailyNotificationSettings
): boolean {
    return (
        a.enabled === b.enabled &&
        JSON.stringify(a.notificationReports) === JSON.stringify(b.notificationReports)
    );
}

function movementCountEnabledFromError(err: unknown): boolean | null {
    if (!err || typeof err !== 'object' || !('data' in err)) {
        return null;
    }
    const body = (err as { data?: unknown }).data;
    if (!body || typeof body !== 'object' || !('data' in body)) {
        return null;
    }
    const payload = (body as { data?: unknown }).data;
    if (!payload || typeof payload !== 'object' || !('enabled' in payload)) {
        return null;
    }
    const enabled = (payload as { enabled?: unknown }).enabled;
    return typeof enabled === 'boolean' ? enabled : null;
}

function loginPolicyEqual(a: LoginPolicy, b: LoginPolicy): boolean {
    return (
        a.enforceSingleDevice === b.enforceSingleDevice &&
        JSON.stringify([...(a.disallowedRolesOnDashboard ?? [])].sort()) ===
            JSON.stringify([...(b.disallowedRolesOnDashboard ?? [])].sort()) &&
        JSON.stringify([...(a.disallowedRolesOnPOS ?? [])].sort()) ===
            JSON.stringify([...(b.disallowedRolesOnPOS ?? [])].sort())
    );
}

export default function BrandSettingsModal({
    isOpen,
    storeBrandId,
    brandName,
    onClose,
}: BrandSettingsModalProps) {
    const [fetchLoading, setFetchLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);

    const [agentConfig, setAgentConfig] = useState<BrandAgentConfig>(DEFAULT_AGENT_CONFIG);
    const [loginPolicy, setLoginPolicy] = useState<LoginPolicy>(DEFAULT_LOGIN_POLICY);
    const [initialAgentConfig, setInitialAgentConfig] =
        useState<BrandAgentConfig>(DEFAULT_AGENT_CONFIG);
    const [initialLoginPolicy, setInitialLoginPolicy] = useState<LoginPolicy>(DEFAULT_LOGIN_POLICY);
    const [movementCountEnabled, setMovementCountEnabled] = useState(false);
    const [initialMovementCountEnabled, setInitialMovementCountEnabled] = useState(false);
    const [rankingLoaded, setRankingLoaded] = useState(false);
    const [rankingLoadFailed, setRankingLoadFailed] = useState(false);
    const rankingSaveSeq = useRef(0);

    const [dailyNotificationSettings, setDailyNotificationSettings] =
        useState<DailyNotificationSettings>(DEFAULT_DAILY_NOTIFICATION_SETTINGS);
    const [initialDailyNotificationSettings, setInitialDailyNotificationSettings] =
        useState<DailyNotificationSettings>(DEFAULT_DAILY_NOTIFICATION_SETTINGS);
    const [availableReports, setAvailableReports] = useState<WhatsAppReportOption[]>([]);

    const [brandOpsConfig, setBrandOpsConfig] =
        useState<StoreBrandOperationsConfig>(DEFAULT_BRAND_OPS_CONFIG);
    const [initialBrandOpsConfig, setInitialBrandOpsConfig] =
        useState<StoreBrandOperationsConfig>(DEFAULT_BRAND_OPS_CONFIG);

    const [dashboardExpiry, setDashboardExpiry] =
        useState<DashboardExpirySettings>(DEFAULT_DASHBOARD_EXPIRY);
    const [initialDashboardExpiry, setInitialDashboardExpiry] =
        useState<DashboardExpirySettings>(DEFAULT_DASHBOARD_EXPIRY);
    const [brandStores, setBrandStores] = useState<StoreSearchResult[]>([]);
    const [makeLiveDialogOpen, setMakeLiveDialogOpen] = useState(false);
    const [selectedMakeLiveIds, setSelectedMakeLiveIds] = useState<string[]>([]);

    const dispatch = useAppDispatch();

    const {
        loading: chatbotSaving,
        callApi: callChatbotApi,
        clearError: clearChatbotError,
    } = useApiClient({ toast: true });
    const {
        loading: policySaving,
        callApi: callPolicyApi,
        clearError: clearPolicyError,
    } = useApiClient({ toast: true });
    const {
        loading: notificationSaving,
        callApi: callNotificationApi,
        clearError: clearNotificationError,
    } = useApiClient({ toast: true });
    const {
        loading: brandOpsSaving,
        callApi: callBrandOpsApi,
        clearError: clearBrandOpsError,
    } = useApiClient({ toast: true });
    const {
        loading: dashboardExpirySaving,
        callApi: callDashboardExpiryApi,
        clearError: clearDashboardExpiryError,
    } = useApiClient({ toast: true });
    const {
        loading: rankingSaving,
        callApi: callRankingApi,
        clearError: clearRankingError,
    } = useApiClient({ toast: true });

    const chatbotDirty = useMemo(
        () => !agentConfigEqual(agentConfig, initialAgentConfig),
        [agentConfig, initialAgentConfig]
    );
    const policyDirty = useMemo(
        () => !loginPolicyEqual(loginPolicy, initialLoginPolicy),
        [loginPolicy, initialLoginPolicy]
    );
    const rankingDirty = useMemo(
        () => movementCountEnabled !== initialMovementCountEnabled,
        [movementCountEnabled, initialMovementCountEnabled]
    );
    const notificationDirty = useMemo(
        () =>
            !dailyNotificationSettingsEqual(
                dailyNotificationSettings,
                initialDailyNotificationSettings
            ),
        [dailyNotificationSettings, initialDailyNotificationSettings]
    );
    const brandOpsDirty = useMemo(
        () =>
            brandOpsConfig.phone !== initialBrandOpsConfig.phone ||
            brandOpsConfig.whatsappNumber !== initialBrandOpsConfig.whatsappNumber,
        [brandOpsConfig, initialBrandOpsConfig]
    );
    const dashboardExpiryDirty = useMemo(
        () => !dashboardExpiryEqual(dashboardExpiry, initialDashboardExpiry),
        [dashboardExpiry, initialDashboardExpiry]
    );
    const trialStores = useMemo(
        () => brandStores.filter((store) => store.storeStatus === STORE_STATUS_TRIAL),
        [brandStores]
    );

    const resetState = useCallback(() => {
        setAgentConfig(DEFAULT_AGENT_CONFIG);
        setLoginPolicy(DEFAULT_LOGIN_POLICY);
        setInitialAgentConfig(DEFAULT_AGENT_CONFIG);
        setInitialLoginPolicy(DEFAULT_LOGIN_POLICY);
        setMovementCountEnabled(false);
        setInitialMovementCountEnabled(false);
        setRankingLoaded(false);
        setRankingLoadFailed(false);
        rankingSaveSeq.current += 1;
        setDailyNotificationSettings(DEFAULT_DAILY_NOTIFICATION_SETTINGS);
        setInitialDailyNotificationSettings(DEFAULT_DAILY_NOTIFICATION_SETTINGS);
        setAvailableReports([]);
        setBrandOpsConfig(DEFAULT_BRAND_OPS_CONFIG);
        setInitialBrandOpsConfig(DEFAULT_BRAND_OPS_CONFIG);
        setDashboardExpiry(DEFAULT_DASHBOARD_EXPIRY);
        setInitialDashboardExpiry(DEFAULT_DASHBOARD_EXPIRY);
        setBrandStores([]);
        setMakeLiveDialogOpen(false);
        setSelectedMakeLiveIds([]);
        setLoadFailed(false);
        clearChatbotError();
        clearPolicyError();
        clearNotificationError();
        clearBrandOpsError();
        clearDashboardExpiryError();
        clearRankingError();
    }, [
        clearChatbotError,
        clearPolicyError,
        clearNotificationError,
        clearBrandOpsError,
        clearDashboardExpiryError,
        clearRankingError,
    ]);

    useEffect(() => {
        if (!isOpen) {
            resetState();
            return;
        }

        let ignoreRanking = false;
        const fetchRanking = async () => {
            setRankingLoaded(false);
            setRankingLoadFailed(false);
            try {
                const rankingRes = await getMovementCountConfig(storeBrandId);
                if (ignoreRanking) {
                    return;
                }
                const enabled = rankingRes.data?.enabled === true;
                setMovementCountEnabled(enabled);
                setInitialMovementCountEnabled(enabled);
                setRankingLoaded(true);
            } catch {
                if (ignoreRanking) {
                    return;
                }
                setRankingLoadFailed(true);
            }
        };

        const fetchData = async () => {
            setFetchLoading(true);
            setLoadFailed(false);
            try {
                const [
                    agentRes,
                    policyRes,
                    notificationRes,
                    reportsRes,
                    brandOpsRes,
                    dashboardExpiryRes,
                    storesRes,
                ] = await Promise.all([
                    getBrandAgentConfig(storeBrandId),
                    getLoginPolicy(storeBrandId),
                    getDailyNotificationSettings(storeBrandId),
                    getStoreBrandDetails(storeBrandId),
                    getStoreBrandOperationsConfig(storeBrandId),
                    getDashboardExpiry(storeBrandId),
                    getStoresByBrand(storeBrandId),
                ]);
                const nextAgent = {
                    ...DEFAULT_AGENT_CONFIG,
                    ...agentRes.data.multiStoreAgentConfig,
                };
                const nextPolicy = normalizeLoginPolicy(policyRes.data);
                const nextNotification: DailyNotificationSettings = {
                    ...DEFAULT_DAILY_NOTIFICATION_SETTINGS,
                    ...notificationRes.data,
                    notificationReports: notificationRes.data?.notificationReports ?? [],
                };
                setAgentConfig(nextAgent);
                setLoginPolicy(nextPolicy);
                setDailyNotificationSettings(nextNotification);
                setInitialDailyNotificationSettings(nextNotification);
                setInitialAgentConfig(nextAgent);
                setInitialLoginPolicy(nextPolicy);
                setAvailableReports(
                    (reportsRes.reports ?? [])
                        .filter((r) => r.kind === 'GENERATED' && r.enabled)
                        .map((r) => ({
                            name: r.name,
                            reportID: r.reportID,
                        }))
                );
                const nextBrandOps: StoreBrandOperationsConfig = {
                    phone: brandOpsRes.data?.operationsConfig?.phone ?? '',
                    whatsappNumber: brandOpsRes.data?.operationsConfig?.whatsappNumber ?? '',
                };
                setBrandOpsConfig(nextBrandOps);
                setInitialBrandOpsConfig(nextBrandOps);
                const nextDashboardExpiry = normalizeDashboardExpiry(dashboardExpiryRes.data);
                setDashboardExpiry(nextDashboardExpiry);
                setInitialDashboardExpiry(nextDashboardExpiry);
                setBrandStores(storesRes ?? []);
            } catch (err: unknown) {
                setLoadFailed(true);
                toast.error(getApiErrorMessage(err));
            } finally {
                setFetchLoading(false);
            }
        };

        fetchRanking();
        fetchData();
        return () => {
            ignoreRanking = true;
        };
    }, [isOpen, storeBrandId, resetState]);

    const toggleDashboardRole = (role: DashboardRole) => {
        setLoginPolicy((prev) => {
            const current = prev.disallowedRolesOnDashboard ?? [];
            const updated = current.includes(role)
                ? current.filter((r) => r !== role)
                : [...current, role];
            return { ...prev, disallowedRolesOnDashboard: updated };
        });
    };

    const togglePOSRole = (role: DashboardRole) => {
        setLoginPolicy((prev) => {
            const current = prev.disallowedRolesOnPOS ?? [];
            const updated = current.includes(role)
                ? current.filter((r) => r !== role)
                : [...current, role];
            return { ...prev, disallowedRolesOnPOS: updated };
        });
    };

    const handleSaveChatbot = async () => {
        clearChatbotError();
        await callChatbotApi(
            () => saveBrandAgentConfig({ ...agentConfig, storeBrandId }),
            () => {
                setInitialAgentConfig(agentConfig);
                toast.success('Your changes have been saved.');
            }
        );
    };

    const handleSavePolicy = async () => {
        clearPolicyError();
        await callPolicyApi(
            () => saveLoginPolicy(storeBrandId, loginPolicy),
            () => {
                setInitialLoginPolicy(loginPolicy);
                toast.success('Your changes have been saved.');
            }
        );
    };

    const handleSaveRanking = async () => {
        clearRankingError();
        rankingSaveSeq.current += 1;
        const seq = rankingSaveSeq.current;
        const enabled = movementCountEnabled;
        await callRankingApi(
            async () => {
                try {
                    return await saveMovementCountConfig(storeBrandId, { enabled });
                } catch (err: unknown) {
                    if (seq !== rankingSaveSeq.current) {
                        return null;
                    }
                    const kept = movementCountEnabledFromError(err);
                    if (kept !== null) {
                        setMovementCountEnabled(kept);
                    }
                    throw err;
                }
            },
            (response) => {
                if (seq !== rankingSaveSeq.current || !response) {
                    return;
                }
                const kept = response.data?.enabled === true;
                setMovementCountEnabled(kept);
                setInitialMovementCountEnabled(kept);
                toast.success('Your changes have been saved.');
            }
        );
    };

    const handleSaveNotification = async () => {
        clearNotificationError();
        await callNotificationApi(
            () => updateDailyNotificationSettings(storeBrandId, dailyNotificationSettings),
            () => {
                setInitialDailyNotificationSettings(dailyNotificationSettings);
                toast.success('Your changes have been saved.');
            }
        );
    };

    const addNotificationReport = () => {
        const newEntry: NotificationReportEntry = {
            reportId: '',
            notificationType: 'Consolidated Report',
            fileType: undefined,
        };
        setDailyNotificationSettings((prev) => ({
            ...prev,
            notificationReports: [...prev.notificationReports, newEntry],
        }));
    };

    const updateNotificationReport = (index: number, patch: Partial<NotificationReportEntry>) => {
        setDailyNotificationSettings((prev) => {
            const updated = prev.notificationReports.map((entry, i) =>
                i === index ? { ...entry, ...patch } : entry
            );
            return { ...prev, notificationReports: updated };
        });
    };

    const removeNotificationReport = (index: number) => {
        setDailyNotificationSettings((prev) => ({
            ...prev,
            notificationReports: prev.notificationReports.filter((_, i) => i !== index),
        }));
    };

    const handleSaveBrandOps = async () => {
        clearBrandOpsError();
        await callBrandOpsApi(
            () =>
                updateStoreBrandOperationsConfig(storeBrandId, {
                    phone: brandOpsConfig.phone?.trim() || null,
                    whatsappNumber: brandOpsConfig.whatsappNumber?.trim() || null,
                }),
            () => {
                setInitialBrandOpsConfig(brandOpsConfig);
                toast.success('Your changes have been saved.');
            }
        );
    };

    const handleRemoveDashboardExpiry = () => {
        setDashboardExpiry({
            expiryDate: null,
            warningMessage: null,
        });
    };

    const persistClearedDashboardExpiry = async (makeLiveStoreIds: string[]) => {
        clearDashboardExpiryError();
        const payload: DashboardExpirySettings = {
            expiryDate: null,
            warningMessage: null,
        };
        await callDashboardExpiryApi(
            () =>
                updateDashboardExpiry(storeBrandId, {
                    ...payload,
                    makeLiveStoreIds,
                }),
            () => {
                setDashboardExpiry(payload);
                setInitialDashboardExpiry(payload);
                setMakeLiveDialogOpen(false);
                setSelectedMakeLiveIds([]);
                setBrandStores((prev) =>
                    prev.map((store) =>
                        makeLiveStoreIds.includes(store._id)
                            ? { ...store, storeStatus: STORE_STATUS_ACTIVE }
                            : store
                    )
                );
                toast.success('Your changes have been saved.');
                dispatch(fetchStores(storeBrandId));
            }
        );
    };

    const handleOpenMakeLiveDialog = () => {
        setSelectedMakeLiveIds(trialStores.map((store) => store._id));
        setMakeLiveDialogOpen(true);
    };

    const handleCancelMakeLiveDialog = () => {
        if (dashboardExpirySaving) {
            return;
        }
        setMakeLiveDialogOpen(false);
        setSelectedMakeLiveIds([]);
    };

    const handleToggleMakeLiveStore = (storeId: string) => {
        setSelectedMakeLiveIds((prev) =>
            prev.includes(storeId) ? prev.filter((id) => id !== storeId) : [...prev, storeId]
        );
    };

    const handleConfirmMakeLiveDialog = async () => {
        await persistClearedDashboardExpiry(selectedMakeLiveIds);
    };

    const handleRemoveDashboardExpiryClick = () => {
        if (trialStores.length > 0) {
            handleOpenMakeLiveDialog();
            return;
        }
        handleRemoveDashboardExpiry();
    };

    const handleSaveDashboardExpiry = async () => {
        const isClearingExpiry =
            dashboardExpiry.expiryDate === null && initialDashboardExpiry.expiryDate !== null;

        if (isClearingExpiry && trialStores.length > 0) {
            handleOpenMakeLiveDialog();
            return;
        }

        clearDashboardExpiryError();
        const payload: DashboardExpirySettings = {
            expiryDate: dashboardExpiry.expiryDate,
            warningMessage: dashboardExpiry.warningMessage?.trim() || null,
        };
        const requestPayload = isClearingExpiry
            ? { ...payload, makeLiveStoreIds: [] as string[] }
            : payload;
        await callDashboardExpiryApi(
            () => updateDashboardExpiry(storeBrandId, requestPayload),
            () => {
                setDashboardExpiry(payload);
                setInitialDashboardExpiry(payload);
                toast.success('Your changes have been saved.');
            }
        );
    };

    const handleBrandSettingsCloseRequest = () => {
        if (makeLiveDialogOpen || dashboardExpirySaving || rankingSaving) {
            return;
        }
        onClose();
    };

    const footer = (
        <Button
            variant="secondary"
            className="flex-1"
            onClick={handleBrandSettingsCloseRequest}
            disabled={
                chatbotSaving ||
                policySaving ||
                notificationSaving ||
                brandOpsSaving ||
                dashboardExpirySaving ||
                rankingSaving ||
                makeLiveDialogOpen
            }
        >
            Close
        </Button>
    );

    return (
        <>
            <ModalShell
                isOpen={isOpen}
                title="Brand settings"
                subtitle={`${brandName} — applies to every store under this brand`}
                onClose={onClose}
                onCloseRequest={handleBrandSettingsCloseRequest}
                footer={footer}
                maxWidth="lg"
            >
                {fetchLoading && <SettingsSkeleton />}

                {loadFailed && !fetchLoading && (
                    <p className="text-sm text-[#64748b] text-center py-6">
                        Could not load settings.
                    </p>
                )}

                {!fetchLoading && !loadFailed && (
                    <>
                        <SettingsSection
                            title="WhatsApp chatbot"
                            description="How customer messages are handled across all stores in this brand."
                            footer={
                                <Button
                                    variant="primary"
                                    isLoading={chatbotSaving}
                                    onClick={handleSaveChatbot}
                                    disabled={!chatbotDirty}
                                >
                                    Save changes
                                </Button>
                            }
                        >
                            <SettingsRow
                                label="Answer delivery enquiries"
                                description="Customers asking about delivery will be handled by the chatbot instead of going to store staff."
                                control={
                                    <Toggle
                                        label="Answer delivery enquiries"
                                        value={agentConfig.checkDeliveryRequest}
                                        onChange={(v) => {
                                            setAgentConfig((prev) => ({
                                                ...prev,
                                                checkDeliveryRequest: v,
                                            }));
                                        }}
                                    />
                                }
                            />
                            <SettingsRow
                                label="Answer bulk order enquiries"
                                description="Customers asking about large or corporate orders will be handled by the chatbot."
                                control={
                                    <Toggle
                                        label="Answer bulk order enquiries"
                                        value={agentConfig.checkBulkOrderRequest}
                                        onChange={(v) => {
                                            setAgentConfig((prev) => ({
                                                ...prev,
                                                checkBulkOrderRequest: v,
                                            }));
                                        }}
                                    />
                                }
                            />
                        </SettingsSection>

                        <SettingsSection
                            title="Search ranking"
                            description="Products that sold more in the last 90 days appear first in search."
                            footer={
                                rankingLoaded ? (
                                    <Button
                                        variant="primary"
                                        isLoading={rankingSaving}
                                        onClick={handleSaveRanking}
                                        disabled={!rankingDirty}
                                    >
                                        Save changes
                                    </Button>
                                ) : undefined
                            }
                        >
                            {rankingLoadFailed ? (
                                <p className="px-4 py-3.5 text-xs text-[#b91c1c]">
                                    Could not load search ranking.
                                </p>
                            ) : (
                                rankingLoaded && (
                                    <SettingsRow
                                        label="Rank by recent sales"
                                        control={
                                            <Toggle
                                                label="Rank by recent sales"
                                                value={movementCountEnabled}
                                                onChange={setMovementCountEnabled}
                                            />
                                        }
                                    />
                                )
                            )}
                        </SettingsSection>

                        <SettingsSection
                            title="Control Login Access"
                            description="Control who can log in and from how many devices."
                            footer={
                                <Button
                                    variant="primary"
                                    isLoading={policySaving}
                                    onClick={handleSavePolicy}
                                    disabled={!policyDirty}
                                >
                                    Save changes
                                </Button>
                            }
                        >
                            <SettingsCheckboxGroup
                                legend="Restrict dashboard access"
                                description="Checked roles will not be able to sign in to the merchant dashboard."
                                options={ALL_DASHBOARD_ROLES}
                                selected={loginPolicy.disallowedRolesOnDashboard ?? []}
                                onToggle={toggleDashboardRole}
                                onSelectAll={() => {
                                    setLoginPolicy((prev) => ({
                                        ...prev,
                                        disallowedRolesOnDashboard: [...ALL_DASHBOARD_ROLES],
                                    }));
                                }}
                                onClearAll={() => {
                                    setLoginPolicy((prev) => ({
                                        ...prev,
                                        disallowedRolesOnDashboard: [],
                                    }));
                                }}
                                name="dashboard-roles"
                            />

                            <SettingsCheckboxGroup
                                legend="Restrict POS access"
                                description="Checked roles will not be able to sign in to the point-of-sale app."
                                options={ALL_DASHBOARD_ROLES}
                                selected={loginPolicy.disallowedRolesOnPOS ?? []}
                                onToggle={togglePOSRole}
                                onSelectAll={() => {
                                    setLoginPolicy((prev) => ({
                                        ...prev,
                                        disallowedRolesOnPOS: [...ALL_DASHBOARD_ROLES],
                                    }));
                                }}
                                onClearAll={() => {
                                    setLoginPolicy((prev) => ({
                                        ...prev,
                                        disallowedRolesOnPOS: [],
                                    }));
                                }}
                                name="pos-roles"
                            />

                            <SettingsRow
                                label="One device per member"
                                description="When a member logs in on a new device, they are signed out everywhere else."
                                control={
                                    <Toggle
                                        label="One device per member"
                                        value={loginPolicy.enforceSingleDevice}
                                        onChange={(v) => {
                                            setLoginPolicy((prev) => ({
                                                ...prev,
                                                enforceSingleDevice: v,
                                            }));
                                        }}
                                    />
                                }
                            />
                        </SettingsSection>

                        <SettingsSection
                            title="Daily Notifications"
                            description="Configure which reports are sent as WhatsApp notifications every day."
                            footer={
                                <Button
                                    variant="primary"
                                    isLoading={notificationSaving}
                                    onClick={handleSaveNotification}
                                    disabled={!notificationDirty}
                                >
                                    Save changes
                                </Button>
                            }
                        >
                            <SettingsRow
                                label="Enable daily notifications"
                                description="Send a WhatsApp message with the selected reports every day."
                                control={
                                    <Toggle
                                        label="Enable daily notifications"
                                        value={dailyNotificationSettings.enabled}
                                        onChange={(v) => {
                                            setDailyNotificationSettings((prev) => ({
                                                ...prev,
                                                enabled: v,
                                            }));
                                        }}
                                    />
                                }
                            />

                            {dailyNotificationSettings.enabled && (
                                <div className="px-4 pb-4 pt-1">
                                    {dailyNotificationSettings.notificationReports.length > 0 && (
                                        <div className="grid grid-cols-[1fr_160px_120px_32px] gap-x-2 mb-1 px-1">
                                            <span className="text-xs font-medium text-[#64748b] uppercase tracking-wide">
                                                Report
                                            </span>
                                            <span className="text-xs font-medium text-[#64748b] uppercase tracking-wide">
                                                Notification type
                                            </span>
                                            <div>
                                                <span className="text-xs font-medium text-[#64748b] uppercase tracking-wide">
                                                    File format
                                                </span>
                                                <p className="text-[10px] text-[#94a3b8] leading-tight mt-0.5">
                                                    Auto = backend default
                                                </p>
                                            </div>
                                            <span />
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        {dailyNotificationSettings.notificationReports.map(
                                            (entry, index) => (
                                                <div
                                                    key={index}
                                                    className="grid grid-cols-[1fr_160px_120px_32px] gap-x-2 items-center"
                                                >
                                                    <select
                                                        value={entry.reportId}
                                                        onChange={(e) =>
                                                            updateNotificationReport(index, {
                                                                reportId: e.target.value,
                                                            })
                                                        }
                                                        className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#1e293b] focus:outline-none focus:ring-2 focus:ring-[#6e64f0]/40 focus:border-[#6e64f0]"
                                                    >
                                                        <option value="">Select report…</option>
                                                        {availableReports.map((r) => (
                                                            <option
                                                                key={r.reportID}
                                                                value={r.reportID}
                                                            >
                                                                {r.name}
                                                            </option>
                                                        ))}
                                                    </select>

                                                    <select
                                                        value={entry.notificationType}
                                                        onChange={(e) =>
                                                            updateNotificationReport(index, {
                                                                notificationType: e.target
                                                                    .value as NotificationReportEntry['notificationType'],
                                                            })
                                                        }
                                                        className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#1e293b] focus:outline-none focus:ring-2 focus:ring-[#6e64f0]/40 focus:border-[#6e64f0]"
                                                    >
                                                        {NOTIFICATION_TYPES.map((t) => (
                                                            <option key={t} value={t}>
                                                                {t}
                                                            </option>
                                                        ))}
                                                    </select>

                                                    <select
                                                        value={entry.fileType ?? ''}
                                                        onChange={(e) =>
                                                            updateNotificationReport(index, {
                                                                fileType:
                                                                    (e.target
                                                                        .value as NotificationReportEntry['fileType']) ||
                                                                    undefined,
                                                            })
                                                        }
                                                        className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#1e293b] focus:outline-none focus:ring-2 focus:ring-[#6e64f0]/40 focus:border-[#6e64f0]"
                                                    >
                                                        <option value="">Auto</option>
                                                        {NOTIFICATION_FILE_TYPES.map((ft) => (
                                                            <option key={ft} value={ft}>
                                                                {ft}
                                                            </option>
                                                        ))}
                                                    </select>

                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            removeNotificationReport(index)
                                                        }
                                                        title="Remove"
                                                        className="flex items-center justify-center w-8 h-8 rounded-lg text-[#94a3b8] hover:text-[#ef4444] hover:bg-[#fef2f2] transition-colors"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                            className="w-4 h-4"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </button>
                                                </div>
                                            )
                                        )}
                                    </div>

                                    {dailyNotificationSettings.notificationReports.length === 0 && (
                                        <div className="flex flex-col items-center justify-center py-6 text-center rounded-xl border border-dashed border-[#e2e8f0] bg-[#fafbfc]">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                className="w-8 h-8 text-[#cbd5e1] mb-2"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.631 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 0 0-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 0 1-2.448-2.448 14.9 14.9 0 0 1 .06-.312m-2.24 2.39a4.493 4.493 0 0 0-1.757 4.306 4.493 4.493 0 0 0 4.306-1.758M16.5 9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z"
                                                />
                                            </svg>
                                            <p className="text-sm font-medium text-[#64748b]">
                                                No reports configured
                                            </p>
                                            <p className="text-xs text-[#94a3b8] mt-0.5">
                                                Add a report below to start receiving daily
                                                notifications.
                                            </p>
                                        </div>
                                    )}

                                    <button
                                        type="button"
                                        onClick={addNotificationReport}
                                        className="mt-3 flex items-center gap-1.5 text-sm font-medium text-[#6e64f0] hover:text-[#5a51d4] px-2 py-1 rounded-lg hover:bg-[#f5f3ff] transition-colors"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                            className="w-4 h-4"
                                        >
                                            <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                                        </svg>
                                        Add report
                                    </button>
                                </div>
                            )}
                        </SettingsSection>

                        <SettingsSection
                            title="Brand Contact Info"
                            description="Phone and WhatsApp number shown on the public microsite for this brand."
                            footer={
                                <Button
                                    variant="primary"
                                    isLoading={brandOpsSaving}
                                    onClick={handleSaveBrandOps}
                                    disabled={!brandOpsDirty}
                                >
                                    Save changes
                                </Button>
                            }
                        >
                            <div className="px-4 py-3.5 space-y-4">
                                <div>
                                    <label
                                        htmlFor="brand-phone"
                                        className="block text-sm font-medium text-[#1e293b]"
                                    >
                                        Phone number
                                    </label>
                                    <p className="text-xs text-[#64748b] mt-0.5">
                                        10-digit number displayed as the brand contact on the
                                        microsite.
                                    </p>
                                    <input
                                        id="brand-phone"
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={10}
                                        value={brandOpsConfig.phone ?? ''}
                                        placeholder="10-digit mobile number"
                                        onChange={(e) => {
                                            const value = normalizePhoneInput(e.target.value);
                                            setBrandOpsConfig((prev) => ({
                                                ...prev,
                                                phone: value,
                                            }));
                                        }}
                                        className="mt-2 w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label
                                        htmlFor="brand-whatsapp"
                                        className="block text-sm font-medium text-[#1e293b]"
                                    >
                                        WhatsApp number
                                    </label>
                                    <p className="text-xs text-[#64748b] mt-0.5">
                                        10-digit WhatsApp number customers can message directly.
                                    </p>
                                    <input
                                        id="brand-whatsapp"
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={10}
                                        value={brandOpsConfig.whatsappNumber ?? ''}
                                        placeholder="10-digit mobile number"
                                        onChange={(e) => {
                                            const value = normalizePhoneInput(e.target.value);
                                            setBrandOpsConfig((prev) => ({
                                                ...prev,
                                                whatsappNumber: value,
                                            }));
                                        }}
                                        className="mt-2 w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </SettingsSection>

                        <SettingsSection
                            title="Dashboard Expiry"
                            description="Schedule when this brand's merchant dashboard stops being usable. Access ends at the end of the selected day (IST)."
                            footer={
                                <Button
                                    variant="primary"
                                    isLoading={dashboardExpirySaving}
                                    onClick={handleSaveDashboardExpiry}
                                    disabled={!dashboardExpiryDirty}
                                >
                                    Save changes
                                </Button>
                            }
                        >
                            <div className="px-4 py-3.5 space-y-4">
                                <div>
                                    <DatePicker
                                        key={dashboardExpiry.expiryDate ?? 'no-expiry'}
                                        label="Expiry date"
                                        value={
                                            dashboardExpiry.expiryDate
                                                ? parseDateOnly(dashboardExpiry.expiryDate)
                                                : undefined
                                        }
                                        onChange={(date) => {
                                            setDashboardExpiry((prev) => ({
                                                ...prev,
                                                expiryDate: date ? formatDateOnly(date) : null,
                                            }));
                                        }}
                                        placeholder="Select expiry date"
                                        minDate={startOfToday()}
                                    />
                                    <p className="text-xs text-[#64748b] mt-1.5">
                                        Leave unset for no scheduled expiry. Access ends at 23:59:59
                                        IST on this date.
                                    </p>
                                    {dashboardExpiry.expiryDate && (
                                        <Button
                                            type="button"
                                            variant="destructive"
                                            size="small"
                                            className="mt-3"
                                            onClick={handleRemoveDashboardExpiryClick}
                                        >
                                            Remove expiry
                                        </Button>
                                    )}
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <label
                                            htmlFor="dashboard-expiry-warning"
                                            className="block text-sm font-medium text-[#1e293b]"
                                        >
                                            Custom warning message (optional)
                                        </label>
                                        <span
                                            className={`text-xs ${
                                                (dashboardExpiry.warningMessage?.length || 0) >
                                                DASHBOARD_EXPIRY_WARNING_MAX_LENGTH
                                                    ? 'text-[#ef4444]'
                                                    : 'text-[#64748b]'
                                            }`}
                                        >
                                            {dashboardExpiry.warningMessage?.length || 0}/
                                            {DASHBOARD_EXPIRY_WARNING_MAX_LENGTH}
                                        </span>
                                    </div>
                                    <textarea
                                        id="dashboard-expiry-warning"
                                        value={dashboardExpiry.warningMessage ?? ''}
                                        onChange={(e) => {
                                            setDashboardExpiry((prev) => ({
                                                ...prev,
                                                warningMessage: e.target.value,
                                            }));
                                        }}
                                        placeholder="Leave blank to use the default warning message"
                                        maxLength={DASHBOARD_EXPIRY_WARNING_MAX_LENGTH}
                                        rows={3}
                                        className="w-full px-4 py-2.5 bg-white border border-[#e2e8f0] rounded-lg text-sm text-[#1e293b] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-transparent resize-none"
                                    />
                                    <p className="text-xs text-[#64748b] mt-1.5">
                                        Shown to store staff in the lead-up to expiry. Leaving this
                                        blank uses the backend default message.
                                    </p>
                                </div>
                            </div>
                        </SettingsSection>
                    </>
                )}
            </ModalShell>
            <MakeTrialStoresLiveDialog
                isOpen={makeLiveDialogOpen}
                stores={trialStores}
                selectedStoreIds={selectedMakeLiveIds}
                isLoading={dashboardExpirySaving}
                onToggleStore={handleToggleMakeLiveStore}
                onConfirm={handleConfirmMakeLiveDialog}
                onCancel={handleCancelMakeLiveDialog}
            />
        </>
    );
}
